import { useEffect, useState } from "react";
import { Truck, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const inputCls = "w-full border border-bone/15 bg-ink px-4 py-3 font-mono text-[11px] tracking-[0.15em] text-bone placeholder:text-bone-muted/60 focus:border-brass/60 focus:outline-none";
const btnCls = "flex items-center gap-2 border border-brass/60 bg-brass/10 px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-brass transition-all hover:bg-brass hover:text-ink disabled:opacity-50";
const TEST_ADDR = { name: "Test Client", email: "test@example.com", line1: "1100 Congress Ave", line2: "", city: "Austin", state: "TX", postal: "78701", country: "US", residential: true };

export default function AdminShipping({ headers }) {
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [creds, setCreds] = useState({ ups_account_number: "", ups_client_id: "", ups_client_secret: "" });
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () => api.get("/admin/shipping", { headers }).then((r) => setCfg(r.data)).catch(() => toast.error(t("admin.saveErr")));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const num = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value === "" ? "" : Number(e.target.value) });
  const txt = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });

  const save = async (e, clear = false) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/shipping", { ...cfg, ...creds, clear_credentials: clear }, { headers });
      setCfg(data);
      setCreds({ ups_account_number: "", ups_client_id: "", ups_client_secret: "" });
      toast.success(t("admin.shippingSaved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin.saveErr"));
    } finally {
      setBusy(false);
    }
  };

  const testConnection = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/shipping/test", TEST_ADDR, { headers });
      setTestResult({ ok: true, ...data });
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.detail || "UPS error" });
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <p className="mt-10 font-mono text-[10px] tracking-[0.3em] text-bone-muted">…</p>;

  return (
    <form data-testid="admin-shipping-panel" onSubmit={save} className="mt-10 space-y-8">
      <section className="border border-bone/10 bg-ink-2 p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-[10px] tracking-[0.3em] text-brass">{t("admin.upsTitle")}</p>
          <span data-testid="admin-ups-status" className={`flex items-center gap-2 border px-3 py-1 font-mono text-[9px] tracking-[0.25em] ${cfg.ups_configured ? "border-brass/50 text-brass" : "border-bone/15 text-bone-muted"}`}>
            <ShieldCheck size={12} /> {cfg.ups_configured ? t("admin.upsConfigured") : t("admin.upsNotConfigured")}
          </span>
        </div>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-bone-dim/80">{t("admin.upsHint")}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <input data-testid="admin-ups-account" placeholder={cfg.ups_account_number_masked ? `${t("admin.upsAccount")} · ${cfg.ups_account_number_masked}` : t("admin.upsAccount")} value={creds.ups_account_number} onChange={(e) => setCreds({ ...creds, ups_account_number: e.target.value })} maxLength={6} autoComplete="off" className={inputCls} />
          <input data-testid="admin-ups-client-id" placeholder={cfg.ups_client_id_masked ? `${t("admin.upsClientId")} · ${cfg.ups_client_id_masked}` : t("admin.upsClientId")} value={creds.ups_client_id} onChange={(e) => setCreds({ ...creds, ups_client_id: e.target.value })} autoComplete="off" className={inputCls} />
          <input data-testid="admin-ups-client-secret" type="password" placeholder={cfg.ups_configured ? `${t("admin.upsClientSecret")} · ••••••••` : t("admin.upsClientSecret")} value={creds.ups_client_secret} onChange={(e) => setCreds({ ...creds, ups_client_secret: e.target.value })} autoComplete="new-password" className={inputCls} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <select data-testid="admin-ups-env" value={cfg.ups_env} onChange={txt("ups_env")} className={`${inputCls} w-auto`}>
            <option value="cie">{t("admin.upsEnvTest")}</option>
            <option value="prod">{t("admin.upsEnvProd")}</option>
          </select>
          <button data-testid="admin-ups-test" type="button" onClick={testConnection} disabled={busy || !cfg.ups_configured} className={btnCls}>
            <Truck size={13} /> {t("admin.upsTest")}
          </button>
          {cfg.ups_configured && (
            <button data-testid="admin-ups-clear" type="button" onClick={(e) => save(e, true)} disabled={busy} className="flex items-center gap-2 border border-bone/15 px-4 py-3 font-mono text-[10px] tracking-[0.25em] text-bone-dim transition-colors hover:border-red-400/50 hover:text-red-300">
              <Trash2 size={12} /> {t("admin.upsClear")}
            </button>
          )}
        </div>
        {testResult && (
          <p data-testid="admin-ups-test-result" className={`mt-4 font-mono text-[10px] tracking-[0.15em] ${testResult.ok ? "text-brass" : "text-red-300"}`}>
            {testResult.ok
              ? `${testResult.service} — ${testResult.amount.toFixed(2)} ${testResult.currency} ≈ ${testResult.shipping_eur.toFixed(2)} € (Cypress → Austin, ${testResult.package.weight} kg)`
              : testResult.error}
          </p>
        )}
      </section>

      <section className="border border-bone/10 bg-ink-2 p-6 sm:p-8">
        <p className="font-mono text-[10px] tracking-[0.3em] text-brass">{t("admin.shipFromTitle")}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <input data-testid="admin-shipfrom-line1" required placeholder={t("checkout.addressPh")} value={cfg.ship_from_line1} onChange={txt("ship_from_line1")} className={`${inputCls} sm:col-span-2`} />
          <input data-testid="admin-shipfrom-city" required placeholder={t("checkout.cityPh")} value={cfg.ship_from_city} onChange={txt("ship_from_city")} className={inputCls} />
          <input data-testid="admin-shipfrom-state" placeholder={t("checkout.statePh")} value={cfg.ship_from_state} onChange={txt("ship_from_state")} className={inputCls} />
          <input data-testid="admin-shipfrom-postal" required placeholder={t("checkout.postalPh")} value={cfg.ship_from_postal} onChange={txt("ship_from_postal")} className={inputCls} />
          <input data-testid="admin-shipfrom-country" required maxLength={2} placeholder="US" value={cfg.ship_from_country} onChange={txt("ship_from_country")} className={inputCls} />
        </div>
      </section>

      <section className="border border-bone/10 bg-ink-2 p-6 sm:p-8">
        <p className="font-mono text-[10px] tracking-[0.3em] text-brass">{t("admin.feesTitle")}</p>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-bone-dim/80">{t("admin.feesHint")}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {[
            ["handling_fee", "fHandling", "0.5", true],
            ["fallback_rate", "fFallback", "0.5", false],
            ["package_weight_kg", "fWeight", "0.1", false],
            ["package_depth_cm", "fDepth", "0.5", false],
          ].map(([k, label, step, required]) => (
            <label key={k} className="block">
              <span className="font-mono text-[9px] tracking-[0.25em] text-bone-muted">{t(`admin.${label}`)}{required ? " *" : ""}</span>
              <input data-testid={`admin-field-${k.replace(/_/g, "-")}`} required={required} type="number" min="0" step={step} value={cfg[k]} onChange={num(k)} className={`${inputCls} mt-2`} />
            </label>
          ))}
        </div>
      </section>

      <button data-testid="admin-shipping-save" type="submit" disabled={busy} className={btnCls}>
        {t("admin.saveSettings")}
      </button>
    </form>
  );
}
